/**
 * Mini App HTTP client — resolves API base URL and attaches optional split-frontend tenant headers.
 * Bundled Jester: same-origin fetch to this server. Split frontend: set VITE_API_BASE_URL.
 */

import brand from '../../config/brand.config';
import {
  isMiniappApiDeferredError,
  MiniappApiDeferredError,
  noteApiReachable,
  noteApiUnreachable,
  shouldDeferApiRequest,
} from '../../utils/apiReachabilityGate';
import { getFrontEndTenantCredentials, isFrontEndTenantConfigured } from './frontEndTenantSession';
import { artificeFetch, resolveArtificeApiUrl, type ArtificeFetchInit } from '../../api/artificeHttp';

export { isMiniappApiDeferredError, MiniappApiDeferredError } from '../../utils/apiReachabilityGate';

export {
  isFrontEndTenantConfigured,
  getFrontEndTenantCredentials,
  setFrontEndTenantCredentials,
  clearFrontEndTenantCredentials,
  seedFrontEndTenantFromLaunchParams,
} from './frontEndTenantSession';

/** Absolute or same-origin path for a Mini App / Grail API route (e.g. /api/miniapp/auth). */
export function resolveMiniappApiUrl(pathOrUrl: string): string {
  return resolveArtificeApiUrl(pathOrUrl, brand.apiBaseUrl);
}

function headersToRecord(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}

/** Attach split-frontend tenant headers when runtime credentials exist. */
export function withFrontEndTenantHeaders(headers?: HeadersInit): Record<string, string> {
  const out = headersToRecord(headers);
  const creds = getFrontEndTenantCredentials();
  if (creds) {
    out['X-FrontEnd-Tenant-Id'] = creds.tenantId;
    out['X-FrontEnd-Tenant-Key'] = creds.tenantKey;
  }
  return out;
}

export type MiniappFetchInit = ArtificeFetchInit;

const DEFAULT_MINIAPP_FETCH_TIMEOUT_MS = 30_000;

/** User-facing copy when fetch() aborts (client timeout or external AbortSignal). */
export function formatMiniappFetchAbortError(timeoutMs: number): string {
  const secs = Math.max(1, Math.round(timeoutMs / 1000));
  return `Request timed out after ${secs}s. The server may still be starting — tap Retry.`;
}

export function isMiniappFetchAbortError(error: unknown): boolean {
  const name = String((error as { name?: string })?.name || '');
  const msg = String((error as { message?: string })?.message || '');
  return name === 'AbortError' || /aborted/i.test(msg);
}

/** Same-origin Grail fetch through the Artifice auth gate, plus Mini App reachability. */
export function miniappFetch(input: string, init?: MiniappFetchInit): Promise<Response> {
  if (shouldDeferApiRequest()) {
    return Promise.reject(new MiniappApiDeferredError());
  }

  const timeoutMs =
    typeof init?.timeoutMs === 'number' && Number.isFinite(init.timeoutMs) && init.timeoutMs > 0
      ? init.timeoutMs
      : DEFAULT_MINIAPP_FETCH_TIMEOUT_MS;

  return artificeFetch(input, {
    ...init,
    timeoutMs,
    apiBaseUrl: init?.apiBaseUrl ?? brand.apiBaseUrl,
  })
    .then((response) => {
      noteApiReachable();
      return response;
    })
    .catch((error) => {
      // Client-side abort/timeouts (stream fallback, navigation) are not server outages.
      if (!isMiniappApiDeferredError(error) && !isMiniappFetchAbortError(error)) {
        noteApiUnreachable();
      }
      throw error;
    });
}

export function getFrontEndTenantId(): string {
  return getFrontEndTenantCredentials()?.tenantId ?? '';
}
